'use client';

import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  ProfileCardHeader,
  Typography,
} from 'src/components';
import {
  CardHeaderContainer,
  CardLoadingIndicator,
  CardTable,
  TableRow,
} from '../Card';
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import useNewGetVolumeByLocation from 'src/hooks/profiles/useNewGetVolumeByLocation';
import useProfileSelections from 'src/hooks/profiles/useProfileSelections';
import LocationRowItem from './LocationRowItem';
import LocationByDay from './LocationByDay';
import { exists } from 'src/utils/exists/exists';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import { InvalidSearchCriteria, NoDataAvailable } from '../NoData';
import { useProfile } from 'src/context/providers/ProfileProvider';

const NewProfileVolumeByLocation = ({
  location = 'hcp',
}: {
  location: string;
}) => {
  const { entityDetails } = useProfile();
  const { providerId } = getEntityIdentifiers(entityDetails);
  const { selectedCohort, selectedFilterSet } = useProfileSelections(location);

  const [expanded, setExpanded] = useState<string[]>([]);

  const { data: volumeByLocationData, isLoading } = useNewGetVolumeByLocation({
    providerId,
    location,
  });

  const topLocations = useMemo(() => {
    // get the first 10 locations
    const locations = volumeByLocationData?.results?.slice(0, 10) || [];
    // locations with percentage over 20
    const topVolumeLocations = locations.filter(
      (location) => location.percentage >= 20 && exists(location.provider_id)
    );

    return topVolumeLocations;
  }, [volumeByLocationData?.results]);

  const otherLocations = useMemo(() => {
    // get the first 10 locations
    const locations = volumeByLocationData?.results?.slice(0, 10) || [];
    // locations with percentage under 20 that have a provider_id
    const lowVolumeLocations = locations?.filter(
      (location) => location.percentage < 20 && location.provider_id
    );
    // null provider entry always goes last in Other Locations (regardless of percentage)
    const comboOtherLocation = locations?.find(
      (location) => !location.provider_id
    );

    if (comboOtherLocation) {
      lowVolumeLocations.push(comboOtherLocation);
    }
    return lowVolumeLocations;
  }, [volumeByLocationData?.results]);

  const currentSavedSearch = useMemo(() => {
    if (!selectedFilterSet && !selectedCohort) return '- All Claims';
    return `- ${selectedFilterSet?.label || selectedCohort?.label} Claims`;
  }, [selectedCohort, selectedFilterSet]);

  // check if the saved search has valid procedure codes
  const isValidSavedSearch = useMemo(() => {
    if (!selectedFilterSet) return true;

    const blockedCodeTypes = ['drug']; // only prescriptions are blocked which are under code type DRUG (not to be confused with DRG codes)

    return selectedFilterSet?.filters?.some(
      (item) => !blockedCodeTypes.includes(item.type?.toLowerCase())
    );
  }, [selectedFilterSet]);

  return (
    <Card className="bg-white p-6">
      <CardHeaderContainer>
        <ProfileCardHeader
          icon={faLocationDot}
          title="Top Locations"
          hasSelectedSavedSearch={!!selectedCohort || !!selectedFilterSet}
          currentSavedSearch={currentSavedSearch}
          subHeader="Percentage of claims volumes* by location attributed; 2025; All Payers."
        />
      </CardHeaderContainer>
      <CardContent className="relative min-h-20">
        {/* Loading overlay */}
        {isLoading && <CardLoadingIndicator />}

        {/* Main content */}
        {!isValidSavedSearch ? (
          <InvalidSearchCriteria />
        ) : volumeByLocationData?.results?.length ? (
          <>
            {topLocations?.map((location, index) => {
              const isFirst = index === 0;
              return (
                <LocationByDay
                  key={`${location.name}-${index}`}
                  isFirst={isFirst}
                  location={location}
                  expanded={expanded}
                  setExpanded={setExpanded}
                />
              );
            })}
            <CardTable>
              <TableRow color="#F0F4F8">
                <Typography
                  variant="body2"
                  bold
                  className="leading-6 text-neutral-900"
                >
                  Other Locations
                </Typography>
                <Typography
                  variant="body2"
                  bold
                  className="leading-6"
                >
                  % of Total
                </Typography>
              </TableRow>
              {otherLocations?.length > 0 ? (
                otherLocations.map((location, index) => (
                  <LocationRowItem key={index} location={location} />
                ))
              ) : (
                <Box className="flex justify-center items-center h-20">
                  <Typography variant="body2" className="text-neutral-500">
                    No data available
                  </Typography>
                </Box>
              )}
            </CardTable>
          </>
        ) : (
          !isLoading && <NoDataAvailable />
        )}
      </CardContent>
      <Box className="py-0.5 px-4">
        <Typography variant="caption" className="text-neutral-500 font-normal">
          Prescription data not available in this widget.
        </Typography>
      </Box>
    </Card>
  );
};

export default NewProfileVolumeByLocation;
